import React, { useState } from 'react';
import { useClasses } from '@/hooks/useClasses'; // Assuming this hook exists
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/hooks/useToast';
import { Calendar, Clock } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

import { useActiveChild } from '@/contexts/ActiveChildContext';
import { useTranslation } from 'react-i18next';

interface Props {
    onComplete: () => void;
    onBack?: () => void;
    targetUserIds?: string[]; // Array of user IDs (for multi-child support)
    targetUserId?: string; // Legacy single user support
}

export const ClassEnrollmentStep: React.FC<Props> = ({ onComplete, onBack, targetUserIds, targetUserId }) => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const { activeChildId } = useActiveChild();

    // Determine effective target - use first user for class filtering (all siblings typically same branch)
    const effectiveUserIds = targetUserIds && targetUserIds.length > 0
        ? targetUserIds
        : targetUserId
            ? [targetUserId]
            : activeChildId
                ? [activeChildId]
                : currentUser?.uid
                    ? [currentUser.uid]
                    : [];

    const effectiveUserId = effectiveUserIds[0];

    const { classes, loading: classesLoading } = useClasses(effectiveUserId);
    const { success, error: showError } = useToast();
    const [enrolling, setEnrolling] = useState<string | null>(null);

    // Filter available classes? (useClasses returns filtered by branch usually)
    const availableClasses = classes || [];

    // Instead of actually enrolling (which would show pending users in class lists),
    // we save the preferred class ID to ALL user documents.
    // Actual enrollment will happen when admin approves the users.
    const handleEnroll = async (classId: string) => {
        if (effectiveUserIds.length === 0) return;
        setEnrolling(classId);
        try {
            // Save preferred class to ALL user docs (deferred enrollment)
            await Promise.all(
                effectiveUserIds.map(userId =>
                    updateDoc(doc(db, 'users', userId), {
                        preferredClassId: classId,
                        updatedAt: new Date()
                    })
                )
            );
            const memberWord = effectiveUserIds.length > 1 ? 'members' : 'member';
            success(`Class selected for ${effectiveUserIds.length} ${memberWord}! Enrollment will be finalized once approved.`);
            onComplete();
        } catch (err) {
            console.error(err);
            showError("Failed to save class preference. Please try again.");
        } finally {
            setEnrolling(null);
        }
    };

    // ... days helper ...
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    return (
        <div className="space-y-6">
            <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">{t('onboarding.classes.title')}</h2>
                <p className="text-muted-foreground">{t('onboarding.classes.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 max-h-[500px] overflow-y-auto">
                {/* ... existing map ... */}
                {classesLoading && <p className="text-center">{t('onboarding.classes.loading')}</p>}

                {!classesLoading && availableClasses.map((cls) => {
                    // Extract first schedule or display default
                    const schedule = cls.schedules && cls.schedules.length > 0 ? cls.schedules[0] : null;
                    const dayName = schedule ? t(`widgets.week_schedule.day_full.${dayKeys[schedule.dayOfWeek]}`) : t('onboarding.classes.tbd');
                    const timeRange = schedule ? `${schedule.startTime} - ${schedule.endTime}` : '';

                    return (
                        <Card key={cls.id} className="hover:border-primary transition-colors cursor-pointer" onClick={() => handleEnroll(cls.id)}>
                            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="space-y-1">
                                    <h3 className="font-semibold text-lg">{cls.name || t('onboarding.classes.untitled')}</h3>
                                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-1">
                                            <Calendar className="h-3.5 w-3.5" />
                                            {dayName}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Clock className="h-3.5 w-3.5" />
                                            {timeRange}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    disabled={enrolling === cls.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEnroll(cls.id);
                                    }}
                                >
                                    {enrolling === cls.id ? t('onboarding.classes.enrolling') : t('schedule.enroll')}
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}


                {!classesLoading && availableClasses.length === 0 && (
                    // ... existing no classes ...
                    <div className="text-center text-muted-foreground p-8 border rounded-lg border-dashed">
                        {t('onboarding.classes.none_scheduled')}
                        <div className="mt-4">
                            <Button variant="outline" onClick={onComplete}>{t('onboarding.classes.skip_dev')}</Button>
                        </div>
                    </div>
                )}
            </div>
            <div className="flex justify-between items-center mt-4">
                {onBack && (
                    <Button variant="ghost" onClick={onBack} className="text-muted-foreground hover:text-foreground">
                        {t('onboarding.classes.back_to_plans')}
                    </Button>
                )}
                <Button variant="ghost" size="sm" onClick={onComplete} className="text-muted-foreground ml-auto">
                    {t('onboarding.classes.skip_for_now')}
                </Button>
            </div>
        </div>
    );
};
